import { Box, Card, CardBody, Heading, Icon, Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaBell, FaBuilding, FaChartLine, FaFileContract, FaFileImport } from "react-icons/fa";
import { useSub } from "../../../common/subscription/useSub";
import { SettingsTab, useQueryParams } from "../../../common/useQueryParams";
import { AssessmentTemplates } from "../../settings/AssessmentTemplates";
import { DocumentTemplates } from "../../settings/DocumentTemplates";
import { InternalParties } from "../../settings/InternalParties";
import { NotificationSettings } from "../../settings/Notifications/NotificationSettings";
import { StandardTerms } from "../../settings/StandardTerms";

const tabs: SettingsTab[] = ["notifications", "document-templates", "internal-parties", "assessment-templates", "standard-terms"];
const tabToIndex = (tab: SettingsTab): number => tabs.indexOf(tab);
const indexToQueryValue = (index: number): SettingsTab => tabs[index];

const PanelCard = ({ children }: { children: ReactNode }) => (
  <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder" boxShadow="sm">
    <CardBody overflowX="auto">{children}</CardBody>
  </Card>
);

export const SettingsPage = () => {
  const { t } = useTranslation();
  const sub = useSub();
  const [tabIndex, setTabIndex] = useState(0);
  const params = useQueryParams();

  useEffect(() => {
    const tab = params.getSettingsTab();
    if (!tab) return;
    setTabIndex(tabToIndex(tab));
  }, [params]);

  return (
    <Box maxW="container.xl" mx="auto">
      <Heading as="h1" size="lg" color="smText" mb="6">
        {t("Settings")}
      </Heading>
      <Tabs
        colorScheme="teal"
        index={tabIndex}
        onChange={(index) => {
          setTabIndex(index);
          params.setSelectedSettingsTab(indexToQueryValue(index));
        }}
        isLazy
      >
        <TabList
          overflowX="auto"
          overflowY="hidden"
          sx={{
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            "& > button": { flexShrink: 0, whiteSpace: "nowrap" },
          }}
        >
          <Tab>
            <Icon as={FaBell} mr="2" />
            {t("Notification Settings")}
          </Tab>
          <Tab isDisabled={!sub.hasDocumentTemplateAccess}>
            <Icon as={FaFileImport} mr="2" />
            {t("Document Templates")}
          </Tab>
          <Tab>
            <Icon as={FaBuilding} mr="2" />
            {t("Internal Parties")}
          </Tab>
          <Tab>
            <Icon as={FaChartLine} mr="2" />
            {t("Assessment Templates")}
          </Tab>
          <Tab>
            <Icon as={FaFileContract} mr="2" />
            {t("Standard terms")}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel px="0" pt="5">
            <PanelCard>
              <NotificationSettings />
            </PanelCard>
          </TabPanel>
          <TabPanel px="0" pt="5">
            <PanelCard>
              <DocumentTemplates />
            </PanelCard>
          </TabPanel>
          <TabPanel px="0" pt="5">
            <PanelCard>
              <InternalParties />
            </PanelCard>
          </TabPanel>
          <TabPanel px="0" pt="5">
            <PanelCard>
              <AssessmentTemplates />
            </PanelCard>
          </TabPanel>
          <TabPanel px="0" pt="5">
            <PanelCard>
              <StandardTerms />
            </PanelCard>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};
